// Structured logging and error tracking
interface LogContext {
  userId?: string;
  sessionId?: string;
  action?: string;
  metadata?: Record<string, any>;
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

class Logger {
  private context: LogContext = {};
  private isDevelopment = import.meta.env.DEV;

  setContext(context: Partial<LogContext>) {
    this.context = { ...this.context, ...context };
  }

  clearContext() {
    this.context = {};
  }

  private log(level: LogLevel, message: string, data?: any) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      ...this.context,
      ...(data && { data }),
    };

    // Console output in development
    if (this.isDevelopment) {
      const consoleMethod = level === 'fatal' || level === 'error' ? 'error' : 
                           level === 'warn' ? 'warn' : 'log';
      console[consoleMethod](`[${level.toUpperCase()}]`, message, data || '');
    }

    // Send to monitoring service in production
    if (!this.isDevelopment && (level === 'error' || level === 'fatal')) {
      this.sendToMonitoring(logEntry);
    }

    return logEntry;
  }

  private async sendToMonitoring(entry: any) {
    try {
      const betterStackToken = import.meta.env.VITE_BETTERSTACK_SOURCE_TOKEN;
      
      if (!betterStackToken) {
        console.warn('Better Stack token not configured');
        return;
      }

      // Send to Better Stack (Logtail)
      await fetch('https://in.logs.betterstack.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${betterStackToken}`,
        },
        body: JSON.stringify({
          dt: entry.timestamp,
          level: entry.level,
          message: entry.message,
          context: {
            userId: entry.userId,
            sessionId: entry.sessionId,
            action: entry.action,
            ...entry.data,
          },
        }),
      });
    } catch (error) {
      console.error('Failed to send log to monitoring service:', error);
    }
  }

  debug(message: string, data?: any) {
    return this.log('debug', message, data);
  }

  info(message: string, data?: any) {
    return this.log('info', message, data);
  }

  warn(message: string, data?: any) {
    return this.log('warn', message, data);
  }

  error(message: string, error?: Error | any) {
    return this.log('error', message, {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } : error,
    });
  }

  fatal(message: string, error?: Error | any) {
    return this.log('fatal', message, {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } : error,
    });
  }
}

export const logger = new Logger();

// Performance monitoring
export class PerformanceMonitor {
  private marks = new Map<string, number>();

  start(label: string) {
    this.marks.set(label, performance.now());
  }

  end(label: string) {
    const startTime = this.marks.get(label);
    if (!startTime) {
      logger.warn(`Performance mark "${label}" not found`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.marks.delete(label);

    logger.info(`Performance: ${label}`, { duration: `${duration.toFixed(2)}ms` });
    return duration;
  }

  measure(label: string, fn: () => any) {
    this.start(label);
    const result = fn();
    this.end(label);
    return result;
  }

  async measureAsync<T>(label: string, fn: () => Promise<T>): Promise<T> {
    this.start(label);
    try {
      const result = await fn();
      this.end(label);
      return result;
    } catch (error) {
      this.end(label);
      throw error;
    }
  }
}

export const perfMonitor = new PerformanceMonitor();
